import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useBills } from '@/hooks/useBills';
import { useToast } from '@/hooks/use-toast';
import { 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Settings, 
  Zap,
  Target,
  BarChart3,
  Clock,
  Users,
  FileText,
  Shield
} from 'lucide-react';

interface Recommendation {
  id: string;
  type: 'optimization' | 'quality' | 'workflow' | 'cost' | 'security';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impact: string;
  confidence: number;
  estimatedImprovement: string;
  action?: () => void;
  implemented?: boolean;
  implementedDate?: string;
}

interface EngineSettings {
  minConfidence: number;
  learningMode: boolean;
  recommendationFrequency: 'real-time' | 'hourly' | 'daily';
  enablePredictive: boolean;
  autoImplement: boolean;
}

export const SmartRecommendationsEngine = () => {
  const { bills, isLoading } = useBills();
  const { toast } = useToast();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [implementedRecommendations, setImplementedRecommendations] = useState<Recommendation[]>([]);
  const [settings, setSettings] = useState<EngineSettings>({
    minConfidence: 75,
    learningMode: true,
    recommendationFrequency: 'real-time',
    enablePredictive: true,
    autoImplement: false
  });

  useEffect(() => {
    if (bills && bills.length > 0) {
      const newRecommendations = generateSmartRecommendations(bills);
      setRecommendations(newRecommendations.filter(r => r.confidence >= settings.minConfidence));
    }
  }, [bills, settings.minConfidence]);

  const generateSmartRecommendations = (billData: any[]): Recommendation[] => {
    const recommendations: Recommendation[] = [];

    // Analyze bill processing patterns
    const totalBills = billData.length;
    const errorRate = billData.filter(bill => bill.status === 'error').length / totalBills;
    const avgConfidence = billData.reduce((sum, bill) => sum + (bill.confidence || 0), 0) / totalBills;
    const manualReviewCount = billData.filter(bill => bill.status === 'manual_review').length;

    // Quality optimization recommendations
    if (errorRate > 0.15) {
      recommendations.push({
        id: 'quality-1',
        type: 'quality',
        priority: 'high',
        title: 'Improve OCR Accuracy',
        description: `Error rate of ${(errorRate * 100).toFixed(1)}% is above optimal threshold. Consider fine-tuning OCR models.`,
        impact: 'Reduce processing errors by 40-60%',
        confidence: 92,
        estimatedImprovement: '40-60% error reduction'
      });
    }

    if (avgConfidence < 0.7) {
      recommendations.push({
        id: 'quality-2',
        type: 'quality',
        priority: 'medium',
        title: 'Enhance Confidence Scoring',
        description: `Average confidence score of ${(avgConfidence * 100).toFixed(1)}% could be improved with model retraining.`,
        impact: 'Increase extraction accuracy by 15-25%',
        confidence: 85,
        estimatedImprovement: '15-25% accuracy boost'
      });
    }

    // Workflow optimization recommendations
    if (manualReviewCount > totalBills * 0.3) {
      recommendations.push({
        id: 'workflow-1',
        type: 'workflow',
        priority: 'high',
        title: 'Reduce Manual Review Backlog',
        description: `${manualReviewCount} bills in manual review queue. Implement automated validation rules.`,
        impact: 'Reduce manual review by 50-70%',
        confidence: 88,
        estimatedImprovement: '50-70% automation increase'
      });
    }

    // Cost optimization recommendations
    const highVolumeSuppliers = billData.reduce((acc, bill) => {
      const supplierName = bill.supplier_name || 'Unknown';
      acc[supplierName] = (acc[supplierName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topSuppliers = Object.entries(highVolumeSuppliers)
      .sort(([,a], [,b]) => (b as number) - (a as number))
      .slice(0, 5);

    if (topSuppliers.length > 0) {
      recommendations.push({
        id: 'cost-1',
        type: 'cost',
        priority: 'medium',
        title: 'Optimize High-Volume Supplier Processing',
        description: `Create dedicated processing templates for top suppliers: ${topSuppliers.map(([name]) => name).join(', ')}.`,
        impact: 'Reduce processing time by 30-45%',
        confidence: 80,
        estimatedImprovement: '30-45% speed improvement'
      });
    }

    // Security recommendations
    const oldBills = billData.filter(bill => {
      const created = new Date(bill.created_at);
      const monthsOld = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24 * 30);
      return monthsOld > 6;
    });

    if (oldBills.length > 100) {
      recommendations.push({
        id: 'security-1',
        type: 'security',
        priority: 'medium',
        title: 'Archive Historical Data',
        description: `${oldBills.length} bills are older than 6 months. Consider archiving for compliance.`,
        impact: 'Improve performance and compliance',
        confidence: 75,
        estimatedImprovement: '20% performance boost'
      });
    }

    return recommendations;
  };

  const implementRecommendation = (recommendation: Recommendation) => {
    const implementedRec = {
      ...recommendation,
      implemented: true,
      implementedDate: new Date().toISOString()
    };

    setImplementedRecommendations(prev => [implementedRec, ...prev.slice(0, 9)]);
    setRecommendations(prev => prev.filter(r => r.id !== recommendation.id));
    
    toast({
      title: "Recommendation Implemented",
      description: `${recommendation.title} has been applied to your system.`,
    });
  };

  const dismissRecommendation = (id: string) => {
    setRecommendations(prev => prev.filter(r => r.id !== id));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'optimization': return Zap;
      case 'quality': return Target;
      case 'workflow': return BarChart3;
      case 'cost': return TrendingUp;
      case 'security': return Shield;
      default: return Brain;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <Brain className="h-5 w-5 animate-spin" />
            <span>Analyzing your data to generate recommendations...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Smart Recommendations Engine</h2>
          <p className="text-muted-foreground">AI-powered suggestions to optimize your bill processing</p>
        </div>
        <Badge variant="outline" className="bg-primary/10">
          <Brain className="h-3 w-3 mr-1" />
          AI-Powered
        </Badge>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active">Active Recommendations</TabsTrigger>
          <TabsTrigger value="implemented">Implemented</TabsTrigger>
          <TabsTrigger value="settings">Engine Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {recommendations.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">All optimized!</h3>
                <p className="text-muted-foreground">
                  No recommendations at this time. Your system is performing well.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {recommendations.map((recommendation) => {
                const IconComponent = getTypeIcon(recommendation.type);
                return (
                  <Card key={recommendation.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          <IconComponent className="h-5 w-5" />
                          <CardTitle className="text-base">{recommendation.title}</CardTitle>
                        </div>
                        <div className="flex space-x-2">
                          <Badge variant={getPriorityColor(recommendation.priority)}>
                            {recommendation.priority}
                          </Badge>
                          <Badge variant="outline">
                            {recommendation.confidence}% confidence
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        {recommendation.description}
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">Expected Impact:</span>
                        </div>
                        <p className="text-sm text-green-600">{recommendation.impact}</p>
                        <p className="text-xs text-muted-foreground">
                          Estimated improvement: {recommendation.estimatedImprovement}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          onClick={() => implementRecommendation(recommendation)}
                          className="flex-1"
                        >
                          Implement
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => dismissRecommendation(recommendation.id)}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="implemented" className="space-y-4">
          {implementedRecommendations.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No implementations yet</h3>
                <p className="text-muted-foreground">
                  Implemented recommendations will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {implementedRecommendations.map((recommendation) => (
                <Card key={recommendation.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{recommendation.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          Implemented on {new Date(recommendation.implementedDate!).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-green-600">
                          {recommendation.estimatedImprovement}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Engine Configuration</CardTitle>
              <CardDescription>
                Customize how the recommendation engine analyzes your data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Minimum Confidence Threshold: {settings.minConfidence}%</Label>
                <Slider
                  value={[settings.minConfidence]}
                  onValueChange={([value]) => setSettings(prev => ({ ...prev, minConfidence: value }))}
                  max={100}
                  min={50}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Only show recommendations with confidence above this threshold
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="learning-mode">Learning Mode</Label>
                  <Switch
                    id="learning-mode"
                    checked={settings.learningMode}
                    onCheckedChange={(checked) => 
                      setSettings(prev => ({ ...prev, learningMode: checked }))
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Allow the engine to learn from your feedback and improve recommendations
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="predictive">Predictive Analytics</Label>
                  <Switch
                    id="predictive"
                    checked={settings.enablePredictive}
                    onCheckedChange={(checked) => 
                      setSettings(prev => ({ ...prev, enablePredictive: checked }))
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enable advanced predictive recommendations based on trends
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="auto-implement">Auto-implement Low-risk</Label>
                  <Switch
                    id="auto-implement"
                    checked={settings.autoImplement}
                    onCheckedChange={(checked) => 
                      setSettings(prev => ({ ...prev, autoImplement: checked }))
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Automatically implement low-risk recommendations with high confidence
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};